import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Upload, Sparkles, ArrowRight, ArrowLeft, Check, Image as ImageIcon } from 'lucide-react';

/**
 * Design Studio — 3-step modal for Print-a-Cap / Tee / Mug.
 *
 * Props:
 *   open, onOpenChange
 *   initialGarment  (optional) — "cap" | "tshirt" | "mug"
 *   initialDesign   (optional) — an existing design to re-edit {garment, template_id, image_data_url, label}
 *   childName       (optional) — used as default label
 *   onSave(design)  — { garment, template_id|null, image_data_url|null, label }
 */

const GARMENTS = [
  { id: 'cap',    label: 'Cap',     emoji: '🧢', desc: 'Custom-printed cap' },
  { id: 'tshirt', label: 'T-Shirt', emoji: '👕', desc: 'Custom-printed tee' },
  { id: 'mug',    label: 'Mug',     emoji: '☕', desc: 'Ceramic mug print' },
];

const TEMPLATES = [
  { id: 'rainbow',    emoji: '🌈', color: '#FF3B6B', label: 'Rainbow' },
  { id: 'unicorn',    emoji: '🦄', color: '#C084FC', label: 'Unicorn' },
  { id: 'dinosaur',   emoji: '🦖', color: '#06D6A0', label: 'Dino'    },
  { id: 'superhero',  emoji: '🦸', color: '#6366F1', label: 'Hero'    },
  { id: 'birthday',   emoji: '🎂', color: '#FFD166', label: 'Cake'    },
  { id: 'space',      emoji: '🚀', color: '#1E1E24', label: 'Space'   },
  { id: 'soccer',     emoji: '⚽', color: '#FF7A2E', label: 'Soccer'  },
  { id: 'sparkle',    emoji: '✨', color: '#EC4899', label: 'Sparkle' },
];

const GARMENT_BG = {
  cap:    { fill: '#FFF4D6', accent: '#FFD166' },
  tshirt: { fill: '#DDF2FE', accent: '#3B82F6' },
  mug:    { fill: '#FFE4E1', accent: '#FF3B6B' },
};

const MAX_IMAGE_BYTES = 500 * 1024; // 500KB

// ---------- Mockup SVGs ----------
const CapMockup = ({ designNode, bg }) => (
  <svg viewBox="0 0 300 240" className="w-full h-full">
    {/* soft background */}
    <rect width="300" height="240" fill={bg} rx="16" />
    {/* Cap crown */}
    <path d="M60 130 C 60 70, 240 70, 240 130 L 240 150 L 60 150 Z" fill={GARMENT_BG.cap.accent} />
    {/* Cap brim */}
    <ellipse cx="150" cy="158" rx="120" ry="14" fill={GARMENT_BG.cap.accent} opacity="0.85" />
    {/* Front panel for design */}
    <rect x="110" y="90" width="80" height="56" rx="6" fill="#FFFFFF" opacity="0.7" />
    <foreignObject x="110" y="90" width="80" height="56">
      <div xmlns="http://www.w3.org/1999/xhtml" className="w-full h-full flex items-center justify-center">
        {designNode}
      </div>
    </foreignObject>
  </svg>
);

const TshirtMockup = ({ designNode, bg }) => (
  <svg viewBox="0 0 300 240" className="w-full h-full">
    <rect width="300" height="240" fill={bg} rx="16" />
    {/* Tee body */}
    <path d="M75 70 L 60 100 L 100 115 L 100 210 L 200 210 L 200 115 L 240 100 L 225 70 L 180 55 Q 150 85 120 55 Z" fill="#FFFFFF" stroke={GARMENT_BG.tshirt.accent} strokeWidth="2" />
    <foreignObject x="115" y="110" width="70" height="70">
      <div xmlns="http://www.w3.org/1999/xhtml" className="w-full h-full flex items-center justify-center">
        {designNode}
      </div>
    </foreignObject>
  </svg>
);

const MugMockup = ({ designNode, bg }) => (
  <svg viewBox="0 0 300 240" className="w-full h-full">
    <rect width="300" height="240" fill={bg} rx="16" />
    {/* Mug body */}
    <rect x="85" y="70" width="130" height="130" rx="10" fill="#FFFFFF" stroke={GARMENT_BG.mug.accent} strokeWidth="2" />
    {/* Handle */}
    <path d="M215 100 Q 255 100 255 135 Q 255 170 215 170" stroke={GARMENT_BG.mug.accent} strokeWidth="6" fill="none" />
    <foreignObject x="100" y="95" width="100" height="80">
      <div xmlns="http://www.w3.org/1999/xhtml" className="w-full h-full flex items-center justify-center">
        {designNode}
      </div>
    </foreignObject>
  </svg>
);

const GarmentMockup = ({ garment, designNode, bg = '#FFFDF8' }) => {
  if (garment === 'tshirt') return <TshirtMockup designNode={designNode} bg={bg} />;
  if (garment === 'mug')    return <MugMockup designNode={designNode} bg={bg} />;
  return <CapMockup designNode={designNode} bg={bg} />;
};

// ---------- Modal ----------
export const DesignStudioModal = ({ open, onOpenChange, initialGarment = 'tshirt', initialDesign = null, childName = '', onSave }) => {
  const [step, setStep] = useState(1);
  const [garment, setGarment] = useState(initialGarment);
  const [templateId, setTemplateId] = useState(null);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [label, setLabel] = useState(childName || '');
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  // Re-edit: when opened with an existing design, pre-load its canvas.
  useEffect(() => {
    if (open && initialDesign) {
      setGarment(initialDesign.garment || initialGarment || 'tshirt');
      setTemplateId(initialDesign.template_id || null);
      setImageDataUrl(initialDesign.image_data_url || null);
      setLabel(initialDesign.label || childName || '');
      setStep(1);
      setError('');
    }
  }, [open, initialDesign, initialGarment, childName]);

  const reset = () => {
    setStep(1);
    setGarment(initialGarment);
    setTemplateId(null);
    setImageDataUrl(null);
    setLabel(childName || '');
    setError('');
  };

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please upload an image file.'); return; }
    if (file.size > MAX_IMAGE_BYTES) { setError('Image must be under 500KB.'); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageDataUrl(e.target.result);
      setTemplateId(null);
    };
    reader.readAsDataURL(file);
  };

  const selectedTemplate = TEMPLATES.find(t => t.id === templateId);
  const designNode = imageDataUrl ? (
    <img src={imageDataUrl} alt="your design" className="max-w-full max-h-full object-contain rounded" />
  ) : selectedTemplate ? (
    <span className="text-[56px] leading-none" aria-hidden>{selectedTemplate.emoji}</span>
  ) : (
    <span className="text-xs text-gray-400 italic">Pick a template</span>
  );

  const canContinueFrom1 = !!garment;
  const canContinueFrom2 = !!templateId || !!imageDataUrl;

  const handleSave = () => {
    if (!canContinueFrom2) return;
    onSave({
      garment,
      template_id: templateId,
      image_data_url: imageDataUrl,
      label: (label || '').slice(0, 40),
      created_at: new Date().toISOString(),
    });
    reset();
    onOpenChange(false);
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white max-w-2xl p-0 overflow-hidden" data-testid="design-studio-modal">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#1E1E24]/10">
          <DialogTitle className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <span className="inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FF3B6B]" /> Design Studio</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B6B7A]">
            Step {step} of 3 — {step === 1 ? 'Pick the item' : step === 2 ? 'Choose a design' : 'Preview & save'}
          </DialogDescription>
          {/* Progress bar */}
          <div className="flex gap-1 mt-2" aria-hidden>
            {[1, 2, 3].map(n => (
              <span key={n} className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: n <= step ? '#FF3B6B' : '#E8E5DF' }} />
            ))}
          </div>
        </DialogHeader>

        <div className="p-6 min-h-[340px]">
          {/* Step 1: Garment */}
          {step === 1 && (
            <div>
              <p className="text-sm text-[#6B6B7A] mb-4">What should we print on?</p>
              <div className="grid grid-cols-3 gap-3">
                {GARMENTS.map(g => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setGarment(g.id)}
                    className={`rounded-2xl p-5 border-2 text-center transition-all hover:-translate-y-0.5 ${garment === g.id ? 'border-[#FF3B6B] bg-[#FFF0F3]' : 'border-[#E8E5DF] bg-white hover:border-[#FF3B6B]/50'}`}
                    data-testid={`design-garment-${g.id}`}
                  >
                    <div className="text-4xl mb-2" aria-hidden>{g.emoji}</div>
                    <p className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{g.label}</p>
                    <p className="text-xs text-[#6B6B7A] mt-1">{g.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Template or Upload */}
          {step === 2 && (
            <div>
              <p className="text-sm text-[#6B6B7A] mb-3">Pick a ready-made template <span className="text-[#9a9aa6]">— or upload your own image (max 500KB)</span></p>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {TEMPLATES.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => { setTemplateId(t.id); setImageDataUrl(null); setError(''); }}
                    className={`rounded-xl p-3 border-2 text-center transition-all ${templateId === t.id ? 'border-[#FF3B6B] bg-[#FFF0F3]' : 'border-[#E8E5DF] bg-white hover:border-[#FF3B6B]/50'}`}
                    data-testid={`design-template-${t.id}`}
                  >
                    <div className="text-3xl" aria-hidden>{t.emoji}</div>
                    <p className="text-[10px] text-[#6B6B7A] mt-1 font-semibold">{t.label}</p>
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3 mb-2">
                <span className="flex-1 border-t border-[#E8E5DF]"></span>
                <span className="text-xs text-[#9a9aa6]">or</span>
                <span className="flex-1 border-t border-[#E8E5DF]"></span>
              </div>

              <div className="flex items-center gap-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="design-upload-input"
                />
                <Button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  variant="outline"
                  className="rounded-full border-[#E8E5DF] text-[#1E1E24] h-10"
                  data-testid="design-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Upload image
                </Button>
                {imageDataUrl && (
                  <span className="inline-flex items-center gap-1 text-xs text-[#06D6A0]">
                    <ImageIcon className="w-4 h-4" /> Image ready
                  </span>
                )}
              </div>
              {error && <p className="text-xs text-[#FF3B6B] mt-2" data-testid="design-error">{error}</p>}
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 3 && (
            <div>
              <p className="text-sm text-[#6B6B7A] mb-4">Here&apos;s how it&apos;ll look. We print on-site during the party.</p>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-5 items-start">
                <div className="rounded-2xl overflow-hidden border border-[#E8E5DF] bg-white aspect-[5/4] max-w-[420px]" data-testid="design-preview">
                  <GarmentMockup garment={garment} designNode={designNode} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B6B7A] block mb-1.5">Name on label (optional)</label>
                  <Input
                    value={label}
                    onChange={(e) => setLabel(e.target.value.slice(0, 40))}
                    placeholder="e.g., Emma"
                    className="rounded-xl border-[#E8E5DF] text-sm"
                    data-testid="design-label-input"
                  />
                  <div className="mt-4 rounded-xl bg-[#FFF0F3] border border-[#FF3B6B]/20 p-3 text-xs text-[#1E1E24]">
                    <p className="font-semibold mb-1">Your design</p>
                    <ul className="space-y-0.5">
                      <li>Item: <b>{GARMENTS.find(g => g.id === garment)?.label}</b></li>
                      <li>Design: <b>{selectedTemplate?.label || (imageDataUrl ? 'Uploaded image' : '—')}</b></li>
                      {label && <li>Label: <b>{label}</b></li>}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#1E1E24]/10 flex items-center justify-between bg-[#FFFDF8]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => (step === 1 ? handleOpenChange(false) : setStep(step - 1))}
            className="text-[#6B6B7A] hover:text-[#1E1E24]"
            data-testid="design-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          {step < 3 ? (
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !canContinueFrom1) || (step === 2 && !canContinueFrom2)}
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white px-5"
              data-testid="design-next-btn"
            >
              Next <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSave}
              className="rounded-full bg-[#06D6A0] hover:bg-[#05b989] text-white px-5"
              data-testid="design-save-btn"
            >
              <Check className="w-4 h-4 mr-1.5" /> Save design
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DesignStudioModal;
